import { useState, useEffect, useMemo } from "react";
import { createAdminApiClient, resolveImageUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Save } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const AdminSettings = () => {
    const [settings, setSettings] = useState({
        id: "site_settings",
        hero_title: "",
        hero_subtitle: "",
        hero_image: "",
        hero_cta_text: "",
        about_text: "",
        about_page_content: "",
        contact_email: "",
        phone: ""
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    const getAdminApi = () => {
        const credStr = sessionStorage.getItem('adminCredentials');
        if (!credStr) return null;
        const credentials = JSON.parse(credStr);
        return createAdminApiClient(credentials.username, credentials.password);
    };
    
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const adminApi = getAdminApi();
                if (!adminApi) {
                    setLoading(false);
                    return;
                }
                const res = await adminApi.getSettings();
                setSettings(res.data);
            } catch (err) {
                console.error("Failed to fetch settings:", err);
                toast.error("Failed to load settings");
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);
    
    const handleSave = async () => {
        setSaving(true);
        try {
            const adminApi = getAdminApi();
            await adminApi.updateSettings(settings);
            toast.success("Settings saved successfully");
        } catch (err) {
            console.error("Failed to save settings:", err);
            toast.error("Failed to save settings");
        } finally {
            setSaving(false);
        }
    };
    
    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    return (
        <div data-testid="admin-settings">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-heading font-black text-3xl tracking-tight mb-2">Site Settings</h1>
                    <p className="text-muted-foreground">Configure global site content and settings</p>
                </div>
                <Button 
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider"
                    data-testid="save-settings-btn"
                >
                    <Save className="mr-2 h-4 w-4" />
                    {saving ? "Saving..." : "Save Settings"}
                </Button>
            </div>
            
            <div className="grid gap-6">
                {/* Hero Section */}
                <Card className="bg-card border border-border rounded-none">
                    <CardContent className="p-6">
                        <h2 className="font-heading font-bold text-lg mb-6">Hero Section</h2>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Hero Title</Label>
                                <Input
                                    value={settings.hero_title}
                                    onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    placeholder="INDUSTRIAL DRY ICE BLASTING"
                                    data-testid="hero-title"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Hero Subtitle</Label>
                                <Textarea
                                    value={settings.hero_subtitle}
                                    onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    rows={2}
                                    placeholder="Professional-grade equipment for demanding applications"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Hero Image URL</Label>
                                <Input
                                    value={settings.hero_image}
                                    onChange={(e) => setSettings({ ...settings, hero_image: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    placeholder="https://example.com/hero.jpg"
                                />
                                {settings.hero_image && (
                                    <div className="mt-2">
                                        <img 
                                            src={resolveImageUrl(settings.hero_image)} 
                                            alt="Hero preview" 
                                            className="h-32 object-cover bg-secondary"
                                        />
                                    </div>
                                )}
                            </div>
                            
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Hero CTA Button Text</Label>
                                <Input
                                    value={settings.hero_cta_text}
                                    onChange={(e) => setSettings({ ...settings, hero_cta_text: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    placeholder="EXPLORE EQUIPMENT"
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                {/* About Section */}
                <Card className="bg-card border border-border rounded-none">
                    <CardContent className="p-6">
                        <h2 className="font-heading font-bold text-lg mb-6">About Page Content</h2>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Footer About Text (short)</Label>
                                <Textarea
                                    value={settings.about_text}
                                    onChange={(e) => setSettings({ ...settings, about_text: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    rows={3}
                                    placeholder="Short description for the footer..."
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">About Us Page (full content)</Label>
                                <p className="text-xs text-muted-foreground mb-2">This is the main content shown on the /about page. Use the editor to format text, add links, bold, etc.</p>
                                <div className="border border-border bg-background">
                                    <ReactQuill
                                        theme="snow"
                                        value={settings.about_page_content || ""}
                                        onChange={val => setSettings({ ...settings, about_page_content: val })}
                                        modules={{ toolbar: [
                                            [{ header: [1, 2, 3, false] }],
                                            ["bold", "italic", "underline"],
                                            [{ list: "ordered" }, { list: "bullet" }],
                                            ["link"],
                                            ["clean"]
                                        ]}}
                                        className="[&_.ql-toolbar]:border-border [&_.ql-container]:border-border [&_.ql-editor]:min-h-[200px]"
                                    />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                {/* Contact Info */}
                <Card className="bg-card border border-border rounded-none">
                    <CardContent className="p-6">
                        <h2 className="font-heading font-bold text-lg mb-6">Contact Information</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Contact Email</Label>
                                <Input
                                    type="email"
                                    value={settings.contact_email}
                                    onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    placeholder="sales@dryicemarketplace.com"
                                    data-testid="contact-email"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Phone Number</Label>
                                <Input
                                    value={settings.phone}
                                    onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    placeholder="1-800-555-1234"
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                {/* Admin Info */}
                <Card className="bg-secondary/50 border border-border rounded-none">
                    <CardContent className="p-6">
                        <h2 className="font-heading font-bold text-lg mb-4">Admin Information</h2>
                        <div className="space-y-2 text-sm text-muted-foreground">
                            <p>
                                <strong className="text-foreground">Default Admin Password:</strong> dryicebulls2024
                            </p>
                            <p>
                                To change the admin password, set the <code className="bg-secondary px-1">ADMIN_PASSWORD</code> environment variable on the server.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default AdminSettings;
